import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InboxIcon from '@mui/icons-material/Inbox';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  type EvalCase,
  useCreateSuiteMutation,
  useDeleteCaseMutation,
  useDeleteSuiteMutation,
  useGetSuiteQuery,
  useListRunsQuery,
  useListSuitesQuery,
  useRunCaseMutation,
  useUpdateCaseMutation,
  useUpdateSuiteMutation,
} from './api/evalApi';
import EvalCaseEditorDialog from './components/EvalCaseEditorDialog';

type EvalRunMode = 'pinned' | 'latest';

type EvalsSearchParamName =
  | 'case_editor'
  | 'case_id'
  | 'edit_case_id'
  | 'fixture_id'
  | 'return_case_editor'
  | 'return_suite_id'
  | 'return_to'
  | 'suite_id'
  | 'tab';

const evalsSearchParamNames: EvalsSearchParamName[] = [
  'case_editor',
  'case_id',
  'edit_case_id',
  'fixture_id',
  'return_case_editor',
  'return_suite_id',
  'return_to',
  'suite_id',
  'tab',
];

const safeIDPattern = /^[A-Za-z0-9._:-]{1,128}$/;

function isSafeEvalsSearchParam(name: EvalsSearchParamName, value: string): boolean {
  switch (name) {
    case 'case_editor':
    case 'return_case_editor':
      return value === '1';
    case 'return_to':
      return value === 'workbench';
    case 'tab':
      return value === 'workbench' || value === 'fixtures' || value === 'runs';
    case 'case_id':
    case 'edit_case_id':
    case 'fixture_id':
    case 'return_suite_id':
    case 'suite_id':
      return safeIDPattern.test(value);
  }
}

function safeEvalsSearchParams(
  source: URLSearchParams,
  options: { delete?: EvalsSearchParamName[] } = {}
): URLSearchParams {
  const deletedParams = new Set(options.delete ?? []);
  const sanitizedParams = new URLSearchParams();

  for (const name of evalsSearchParamNames) {
    if (deletedParams.has(name)) {
      continue;
    }
    const value = source.get(name);
    if (value && isSafeEvalsSearchParam(name, value)) {
      sanitizedParams.set(name, value);
    }
  }

  return sanitizedParams;
}

export default function EvalsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data, isLoading, isFetching, error, refetch } = useListSuitesQuery();
  const [selectedSuiteID, setSelectedSuiteID] = useState<string | null>(null);
  const [newSuiteName, setNewSuiteName] = useState('');
  const [newSuiteDescription, setNewSuiteDescription] = useState('');
  const [showDescriptionField, setShowDescriptionField] = useState(false);
  const [suiteDescriptionDraft, setSuiteDescriptionDraft] = useState('');
  const [suiteProvidersDraft, setSuiteProvidersDraft] = useState('');
  const [suiteProfileSlugDraft, setSuiteProfileSlugDraft] = useState('');
  const [showOnlyStale, setShowOnlyStale] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingCase, setEditingCase] = useState<EvalCase | null>(null);
  const [createSuite, { isLoading: isCreatingSuite }] = useCreateSuiteMutation();
  const [runCase, { isLoading: isEnqueueingRun }] = useRunCaseMutation();
  const [updateCase, { isLoading: isUpdatingCase }] = useUpdateCaseMutation();
  const [updateSuite, { isLoading: isUpdatingSuite }] = useUpdateSuiteMutation();
  const [deleteSuite, { isLoading: isDeletingSuite }] = useDeleteSuiteMutation();
  const [deleteCase, { isLoading: isDeletingCase }] = useDeleteCaseMutation();

  const suites = data?.suites ?? [];
  const activeSuiteID = selectedSuiteID ?? suites[0]?.id ?? null;
  const {
    data: suiteData,
    isLoading: isLoadingSuite,
    error: suiteError,
  } = useGetSuiteQuery(activeSuiteID ?? '', {
    skip: !activeSuiteID,
  });

  const selectedSuite = suiteData?.suite;
  const selectedSuiteCases = suiteData?.cases ?? [];

  const { data: needsReviewQueueData } = useListRunsQuery({ requires_review: true, limit: 1 });
  const { data: regressionQueueData } = useListRunsQuery({ is_regression: true, limit: 1 });

  const staleCaseCount = useMemo(
    () => selectedSuiteCases.filter((testCase) => testCase.stale).length,
    [selectedSuiteCases]
  );

  const needsReviewCount = needsReviewQueueData?.total_count ?? 0;
  const regressionCount = regressionQueueData?.total_count ?? 0;

  const visibleCases = useMemo(
    () =>
      showOnlyStale ? selectedSuiteCases.filter((testCase) => testCase.stale) : selectedSuiteCases,
    [selectedSuiteCases, showOnlyStale]
  );

  useEffect(() => {
    const suiteIDFromParams = searchParams.get('suite_id');
    if (!suiteIDFromParams || selectedSuiteID) {
      return;
    }
    if (!isSafeEvalsSearchParam('suite_id', suiteIDFromParams)) {
      const params = safeEvalsSearchParams(searchParams, { delete: ['suite_id'] });
      navigate({ pathname: '/admin/evals', search: params.toString() }, { replace: true });
      return;
    }
    setSelectedSuiteID(suiteIDFromParams);
  }, [navigate, searchParams, selectedSuiteID]);

  useEffect(() => {
    setSuiteDescriptionDraft(selectedSuite?.description ?? '');
    setSuiteProvidersDraft((selectedSuite?.default_providers ?? []).join(', '));
    setSuiteProfileSlugDraft(selectedSuite?.default_profile_slug ?? '');
  }, [selectedSuite]);

  useEffect(() => {
    if (
      searchParams.get('case_editor') !== '1' ||
      searchParams.get('edit_case_id') ||
      !activeSuiteID ||
      isEditorOpen
    ) {
      return;
    }

    setEditingCase(null);
    setIsEditorOpen(true);

    const params = safeEvalsSearchParams(searchParams, { delete: ['case_editor'] });
    navigate({ pathname: '/admin/evals', search: params.toString() }, { replace: true });
  }, [activeSuiteID, isEditorOpen, navigate, searchParams]);

  useEffect(() => {
    const editCaseID = searchParams.get('edit_case_id');
    if (!editCaseID || !activeSuiteID || isEditorOpen || isLoadingSuite) {
      return;
    }

    const caseToEdit = selectedSuiteCases.find((testCase) => testCase.id === editCaseID);
    if (!caseToEdit) {
      return;
    }

    setEditingCase(caseToEdit);
    setIsEditorOpen(true);

    const params = safeEvalsSearchParams(searchParams, { delete: ['edit_case_id'] });
    navigate({ pathname: '/admin/evals', search: params.toString() }, { replace: true });
  }, [activeSuiteID, isEditorOpen, isLoadingSuite, navigate, searchParams, selectedSuiteCases]);

  const resetSuiteSettings = () => {
    setSuiteDescriptionDraft(selectedSuite?.description ?? '');
    setSuiteProvidersDraft((selectedSuite?.default_providers ?? []).join(', '));
    setSuiteProfileSlugDraft(selectedSuite?.default_profile_slug ?? '');
  };

  const handleCreateSuite = async () => {
    const name = newSuiteName.trim();
    if (!name) {
      setStatusMessage('Suite name is required.');
      return;
    }

    try {
      const response = await createSuite({
        name,
        description: newSuiteDescription.trim(),
        tags: [],
        default_providers: [],
      }).unwrap();
      setSelectedSuiteID(response.suite.id);
      setNewSuiteName('');
      setNewSuiteDescription('');
      setShowDescriptionField(false);
      setStatusMessage(`Created suite "${response.suite.name}".`);
    } catch (createError: any) {
      setStatusMessage(createError?.data?.error ?? 'Failed to create suite.');
    }
  };

  const handleSaveSuiteSettings = async () => {
    if (!activeSuiteID) {
      return;
    }

    const providers = suiteProvidersDraft
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    try {
      const response = await updateSuite({
        id: activeSuiteID,
        description: suiteDescriptionDraft.trim(),
        default_providers: providers,
        default_profile_slug: suiteProfileSlugDraft.trim() || undefined,
      }).unwrap();
      setStatusMessage(`Updated suite "${response.suite.name}".`);
      refetch();
    } catch (updateError: any) {
      setStatusMessage(updateError?.data?.error ?? 'Failed to update suite settings.');
    }
  };

  const handleDeleteSuite = async () => {
    if (!activeSuiteID || !selectedSuite) {
      return;
    }

    const confirmed = window.confirm(
      `Delete suite "${selectedSuite.name}"? This only works when the suite has no cases.`
    );
    if (!confirmed) {
      return;
    }

    try {
      await deleteSuite({ id: activeSuiteID }).unwrap();
      setStatusMessage(`Deleted suite "${selectedSuite.name}".`);
      setSelectedSuiteID(null);
      refetch();
    } catch (deleteError: any) {
      setStatusMessage(deleteError?.data?.error ?? 'Failed to delete suite.');
    }
  };

  const handleRunCase = async (caseID: string, runMode: EvalRunMode = 'pinned') => {
    try {
      const response = await runCase({
        case_id: caseID,
        triggered_by: 'manual',
        run_mode: runMode,
      }).unwrap();
      setStatusMessage(`Queued ${response.run_ids.length} ${runMode} run(s) for case ${caseID}.`);
    } catch (runError: any) {
      setStatusMessage(runError?.data?.error ?? 'Failed to queue eval run.');
    }
  };

  const handleRunStaleCases = async () => {
    const staleCases = selectedSuiteCases.filter((testCase) => testCase.stale && testCase.enabled);
    if (staleCases.length === 0) {
      setStatusMessage('No enabled stale cases to run.');
      return;
    }

    try {
      let runCount = 0;
      for (const testCase of staleCases) {
        const response = await runCase({
          case_id: testCase.id,
          triggered_by: 'manual',
          run_mode: 'pinned',
        }).unwrap();
        runCount += response.run_ids.length;
      }
      setStatusMessage(`Queued ${runCount} run(s) across ${staleCases.length} stale case(s).`);
    } catch (runError: any) {
      setStatusMessage(runError?.data?.error ?? 'Failed to queue stale case runs.');
    }
  };

  const handleToggleCaseEnabled = async (testCase: EvalCase) => {
    try {
      const updatedEnabled = !testCase.enabled;
      const response = await updateCase({ id: testCase.id, enabled: updatedEnabled }).unwrap();
      setStatusMessage(`Case "${response.case.name}" ${updatedEnabled ? 'enabled' : 'disabled'}.`);
      refetch();
    } catch (updateError: any) {
      setStatusMessage(updateError?.data?.error ?? 'Failed to update case state.');
    }
  };

  const handleMarkCaseFresh = async (testCase: EvalCase) => {
    try {
      const response = await updateCase({ id: testCase.id, stale: false }).unwrap();
      setStatusMessage(`Case "${response.case.name}" marked fresh.`);
      refetch();
    } catch (updateError: any) {
      setStatusMessage(updateError?.data?.error ?? 'Failed to mark case fresh.');
    }
  };

  const handleDeleteCase = async (testCase: EvalCase) => {
    const confirmed = window.confirm(
      `Delete case "${testCase.name}"? This only works when the case has no runs.`
    );
    if (!confirmed) {
      return;
    }

    try {
      await deleteCase({ id: testCase.id, suite_id: testCase.suite_id }).unwrap();
      setStatusMessage(`Deleted case "${testCase.name}".`);
      refetch();
    } catch (deleteError: any) {
      setStatusMessage(deleteError?.data?.error ?? 'Failed to delete case.');
    }
  };

  const handleOpenCreateCase = () => {
    setEditingCase(null);
    setIsEditorOpen(true);
  };

  const handleManageFixtures = (fixtureId?: string) => {
    const params = safeEvalsSearchParams(searchParams);
    params.set('tab', 'fixtures');
    params.set('return_to', 'workbench');
    if (activeSuiteID) {
      params.set('return_suite_id', activeSuiteID);
      params.set('suite_id', activeSuiteID);
    }
    params.set('return_case_editor', '1');
    if (fixtureId && isSafeEvalsSearchParam('fixture_id', fixtureId)) {
      params.set('fixture_id', fixtureId);
    } else {
      params.delete('fixture_id');
    }
    navigate({ pathname: '/admin/evals', search: params.toString() });
  };

  const handleOpenEditCase = (testCase: EvalCase) => {
    setEditingCase(testCase);
    setIsEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setEditingCase(null);
  };

  const handleSavedCase = () => {
    refetch();
    setEditingCase(null);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Create eval suites, inspect cases, and trigger manual runs.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Refresh
        </Button>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Tooltip title="Runs where the AI was uncertain and a human should verify">
          <span>
            <Chip
              label={`${needsReviewCount} need review`}
              size="small"
              color={needsReviewCount > 0 ? 'warning' : 'default'}
            />
          </span>
        </Tooltip>
        <Tooltip title="Cases that previously passed but now fail">
          <span>
            <Chip
              label={`${regressionCount} regressions`}
              size="small"
              color={regressionCount > 0 ? 'error' : 'default'}
            />
          </span>
        </Tooltip>
      </Stack>

      {statusMessage && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setStatusMessage(null)}>
          {statusMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as any)?.data?.error ?? 'Failed to load eval suites.'}
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* New Suite — compact single row */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            placeholder="New suite name"
            value={newSuiteName}
            onChange={(event) => setNewSuiteName(event.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleCreateSuite();
            }}
          />
          <Button
            variant="text"
            size="small"
            onClick={() => setShowDescriptionField((prev) => !prev)}
          >
            {showDescriptionField ? 'Hide description' : '+ Description'}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreateSuite}
            disabled={isCreatingSuite}
            sx={{ minWidth: 120 }}
          >
            {isCreatingSuite ? 'Creating...' : 'Create Suite'}
          </Button>
        </Stack>
        <Collapse in={showDescriptionField}>
          <TextField
            placeholder="Suite description (optional)"
            value={newSuiteDescription}
            onChange={(event) => setNewSuiteDescription(event.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1.5 }}
          />
        </Collapse>
      </Paper>

      {/* Two-panel layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 2 }}>
        {/* Suite List */}
        <Paper variant="outlined" sx={{ maxHeight: 640, overflow: 'auto' }}>
          {isLoading ? (
            <Stack spacing={1} sx={{ p: 2 }}>
              <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
            </Stack>
          ) : suites.length === 0 ? (
            <Box
              sx={{
                py: 6,
                px: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="subtitle1" gutterBottom>
                No eval suites yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create your first eval suite to start testing prompts
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  const nameField = document.querySelector<HTMLInputElement>(
                    'input[placeholder="New suite name"]'
                  );
                  nameField?.focus();
                }}
              >
                Create Suite
              </Button>
            </Box>
          ) : (
            <List disablePadding>
              {suites.map((suite) => (
                <ListItemButton
                  key={suite.id}
                  selected={suite.id === activeSuiteID}
                  onClick={() => {
                    setSelectedSuiteID(suite.id);
                    setShowOnlyStale(false);
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                          {suite.name}
                        </Typography>
                      </Stack>
                    }
                    secondary={suite.description || 'No description'}
                    secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>

        {/* Detail Panel */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          {!activeSuiteID ? (
            <Box
              sx={{
                py: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="body2" color="text.secondary">
                Select a suite to view cases.
              </Typography>
            </Box>
          ) : isLoadingSuite ? (
            <Stack spacing={2} sx={{ p: 2 }}>
              <Skeleton variant="rectangular" height={32} width="40%" sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={16} width="60%" sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, mt: 2 }} />
            </Stack>
          ) : suiteError ? (
            <Alert severity="error">Failed to load suite details.</Alert>
          ) : (
            <>
              {/* Suite header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h5">{selectedSuite?.name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {selectedSuite?.description || 'No description'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Chip label={`${selectedSuiteCases.length} case(s)`} size="small" />
                  <Chip
                    label={`${staleCaseCount} stale`}
                    size="small"
                    color={staleCaseCount > 0 ? 'warning' : 'default'}
                  />
                </Stack>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Suite Settings — collapsible accordion */}
              <Accordion
                variant="outlined"
                disableGutters
                sx={{
                  mb: 2,
                  '&:before': { display: 'none' },
                  borderRadius: 1,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SettingsIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2">Suite Settings</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.5}>
                    <TextField
                      label="Description"
                      size="small"
                      value={suiteDescriptionDraft}
                      onChange={(event) => setSuiteDescriptionDraft(event.target.value)}
                    />
                    <TextField
                      label="Default Providers (comma separated)"
                      size="small"
                      value={suiteProvidersDraft}
                      onChange={(event) => setSuiteProvidersDraft(event.target.value)}
                      helperText="LLM providers to test against when a case doesn't specify its own (e.g. anthropic:claude-sonnet-5-5)"
                    />
                    <TextField
                      label="Default Profile Slug"
                      size="small"
                      value={suiteProfileSlugDraft}
                      onChange={(event) => setSuiteProfileSlugDraft(event.target.value)}
                      helperText="The system profile that shapes how the AI responds for this suite"
                    />
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" size="small" onClick={resetSuiteSettings}>
                        Reset
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveSuiteSettings}
                        disabled={isUpdatingSuite}
                      >
                        {isUpdatingSuite ? 'Saving...' : 'Save Settings'}
                      </Button>
                      <Box sx={{ flex: 1 }} />
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={handleDeleteSuite}
                        disabled={isDeletingSuite}
                      >
                        Delete Suite
                      </Button>
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Divider sx={{ mb: 2 }} />

              {/* Case actions toolbar */}
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateCase}
                >
                  Create Case
                </Button>
                <Tooltip title="Re-run all cases that haven't been verified since the prompt changed">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      onClick={handleRunStaleCases}
                      disabled={isEnqueueingRun || staleCaseCount === 0}
                    >
                      Run Stale
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant={showOnlyStale ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setShowOnlyStale((current) => !current)}
                >
                  {showOnlyStale ? 'Show All' : 'Stale Only'}
                </Button>
              </Stack>

              {/* Case list */}
              {visibleCases.length === 0 ? (
                <Box
                  sx={{
                    py: 6,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <InboxIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    {showOnlyStale ? 'No stale cases' : 'No cases yet'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {showOnlyStale
                      ? 'No stale cases in this suite.'
                      : 'Create a case to start evaluating prompts.'}
                  </Typography>
                  {!showOnlyStale && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleOpenCreateCase}
                      sx={{ mt: 2 }}
                    >
                      Create Case
                    </Button>
                  )}
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {visibleCases.map((testCase) => (
                    <Paper key={testCase.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography
                              variant="subtitle2"
                              sx={{
                                opacity: testCase.enabled ? 1 : 0.5,
                              }}
                            >
                              {testCase.name}
                            </Typography>
                            {!testCase.enabled && (
                              <Tooltip title="This case won't run during suite-wide test runs">
                                <span>
                                  <Chip label="Disabled" size="small" variant="outlined" />
                                </span>
                              </Tooltip>
                            )}
                            {testCase.stale && (
                              <Tooltip title="The prompt changed since this case last ran — re-run to verify">
                                <span>
                                  <Chip label="Stale" color="warning" size="small" />
                                </span>
                              </Tooltip>
                            )}
                            {testCase.assertions.length > 0 && (
                              <Chip
                                label={`${testCase.assertions.length} assertion${testCase.assertions.length !== 1 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                                color="info"
                              />
                            )}
                            {testCase.target_config.providers.length > 0 && (
                              <Chip
                                label={`${testCase.target_config.providers.length} provider${testCase.target_config.providers.length !== 1 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {testCase.description || 'No description'}
                          </Typography>
                        </Box>

                        {/* Actions */}
                        <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenEditCase(testCase)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              testCase.enabled ? 'Run pinned prompt version' : 'Enable case to run'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleRunCase(testCase.id)}
                                disabled={isEnqueueingRun || !testCase.enabled}
                                aria-label={`Run Pinned ${testCase.name}`}
                              >
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              testCase.enabled
                                ? 'Run against latest published prompt version'
                                : 'Enable case to run'
                            }
                          >
                            <span>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => handleRunCase(testCase.id, 'latest')}
                                disabled={isEnqueueingRun || !testCase.enabled}
                                sx={{
                                  minWidth: 'auto',
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                }}
                              >
                                Run Latest
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={`Delete Case ${testCase.name}`}
                              onClick={() => handleDeleteCase(testCase)}
                              disabled={isDeletingCase}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                          <Tooltip title="Stop or resume including this case in runs">
                            <span>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => handleToggleCaseEnabled(testCase)}
                                disabled={isUpdatingCase}
                                sx={{
                                  minWidth: 'auto',
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                }}
                              >
                                {testCase.enabled ? 'Disable' : 'Enable'}
                              </Button>
                            </span>
                          </Tooltip>
                          {testCase.stale && (
                            <Tooltip title="Clear the stale warning without re-running">
                              <span>
                                <Button
                                  variant="text"
                                  size="small"
                                  color="warning"
                                  onClick={() => handleMarkCaseFresh(testCase)}
                                  disabled={isUpdatingCase}
                                  sx={{
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  Mark Fresh
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          <Button
                            variant="text"
                            size="small"
                            onClick={() =>
                              navigate(
                                `/admin/evals?tab=runs&suite_id=${encodeURIComponent(testCase.suite_id)}&case_id=${encodeURIComponent(testCase.id)}`
                              )
                            }
                            sx={{ minWidth: 'auto', textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Runs
                          </Button>
                        </Stack>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Paper>
      </Box>

      {activeSuiteID && (
        <EvalCaseEditorDialog
          open={isEditorOpen}
          suiteId={activeSuiteID}
          suitePromptId={selectedSuite?.prompt_id}
          defaultProviders={selectedSuite?.default_providers ?? []}
          defaultProfileSlug={selectedSuite?.default_profile_slug}
          preferredFixtureSetId={searchParams.get('fixture_id') ?? undefined}
          initialCase={editingCase ?? undefined}
          onManageFixtures={handleManageFixtures}
          onClose={handleCloseEditor}
          onSaved={handleSavedCase}
        />
      )}
    </Box>
  );
}
